import React, { useState, useEffect } from 'react';
import {
  Key,
  Plus,
  Search,
  RefreshCw,
  Trash2,
  Edit,
  Copy,
  Shield,
  Clock,
  Activity,
  AlertTriangle,
  Check,
  X,
} from 'lucide-react';
import { useAuth } from 'hooks/useAuth';
import { apiKeyService } from 'api/services/developer.service';
import {
  ApiKeyDTO,
  ApiKeyCreateResponse,
  CreateApiKeyRequest,
  ApiKeyStatsDTO,
  ApiKeyType,
} from 'types/developer';
import OrganizationFilter from 'components/Filters/OrganizationFilter';

// Scope options for the form
const SCOPE_OPTIONS = ['READ', 'WRITE', 'ADMIN'];

const ApiKeys: React.FC = () => {
  const { user, currentOrganization } = useAuth();
  const userRole = (currentOrganization?.role || user?.role || '').toLowerCase();
  const isMasterAdmin = userRole === 'master_admin';

  const [apiKeys, setApiKeys] = useState<ApiKeyDTO[]>([]);
  const [stats, setStats] = useState<ApiKeyStatsDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<string>('');
  const [organizationId, setOrganizationId] = useState<string>(
    isMasterAdmin ? '' : currentOrganization?.id || ''
  );
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSecretModal, setShowSecretModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedKey, setSelectedKey] = useState<ApiKeyDTO | null>(null);
  const [newKeySecret, setNewKeySecret] = useState<string>('');
  const [secretCopied, setSecretCopied] = useState(false);

  // Form state
  const [formData, setFormData] = useState<CreateApiKeyRequest>({
    name: '',
    keyType: ApiKeyType.BUSINESS_KEY,
    scopes: ['READ'],
    description: '',
  });

  useEffect(() => {
    if (organizationId) {
      fetchApiKeys();
      fetchStats();
    }
  }, [organizationId, activeFilter, page]);

  const fetchApiKeys = async () => {
    try {
      setLoading(true);
      const response = await apiKeyService.list({
        organizationId: organizationId || currentOrganization?.id || '',
        active: activeFilter === '' ? undefined : activeFilter === 'active',
        page,
        size: 10,
      });
      if (response.success && response.data) {
        setApiKeys(response.data.content);
        setTotalPages(response.data.totalPages);
      } else {
        console.error('Failed to fetch API keys:', response.error);
      }
    } catch (error) {
      console.error('Failed to fetch API keys:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await apiKeyService.getStats(organizationId || currentOrganization?.id || '');
      if (response.success && response.data) {
        setStats(response.data);
      } else {
        console.error('Failed to fetch stats:', response.error);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleCreate = async () => {
    try {
      const response = await apiKeyService.create(
        String(user?.id || ''),
        organizationId || currentOrganization?.id || '',
        formData
      );
      if (response.success && response.data) {
        setNewKeySecret(response.data.secret);
        setShowCreateModal(false);
        setShowSecretModal(true);
        fetchApiKeys();
        fetchStats();
        resetForm();
      } else {
        console.error('Failed to create API key:', response.error);
      }
    } catch (error) {
      console.error('Failed to create API key:', error);
    }
  };

  const handleRevoke = async (key: ApiKeyDTO) => {
    try {
      const response = await apiKeyService.revoke(key.id);
      if (response.success) {
        fetchApiKeys();
        fetchStats();
      } else {
        console.error('Failed to revoke API key:', response.error);
      }
    } catch (error) {
      console.error('Failed to revoke API key:', error);
    }
  };

  const handleDelete = async () => {
    if (!selectedKey) return;
    try {
      const response = await apiKeyService.delete(selectedKey.id);
      if (response.success) {
        setShowDeleteModal(false);
        setSelectedKey(null);
        fetchApiKeys();
        fetchStats();
      } else {
        console.error('Failed to delete API key:', response.error);
      }
    } catch (error) {
      console.error('Failed to delete API key:', error);
    }
  };

  const handleRegenerate = async (key: ApiKeyDTO) => {
    try {
      const response = await apiKeyService.regenerate(key.id);
      if (response.success && response.data) {
        setNewKeySecret(response.data.secret);
        setShowSecretModal(true);
        fetchApiKeys();
      } else {
        console.error('Failed to regenerate API key:', response.error);
      }
    } catch (error) {
      console.error('Failed to regenerate API key:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      keyType: ApiKeyType.BUSINESS_KEY,
      scopes: ['READ'],
      description: '',
    });
    setSelectedKey(null);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSecretCopied(true);
    setTimeout(() => setSecretCopied(false), 2000);
  };

  const toggleScope = (scope: string) => {
    const currentScopes = formData.scopes || [];
    if (currentScopes.includes(scope)) {
      setFormData({
        ...formData,
        scopes: currentScopes.filter((s) => s !== scope),
      });
    } else {
      setFormData({
        ...formData,
        scopes: [...currentScopes, scope],
      });
    }
  };

  const getStatusBadge = (active: boolean, expiresAt?: string) => {
    const isExpired = expiresAt && new Date(expiresAt) < new Date();
    if (isExpired) {
      return (
        <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          EXPIRED
        </span>
      );
    }
    return active ? (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
        ACTIVE
      </span>
    ) : (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
        REVOKED
      </span>
    );
  };

  const getScopeBadge = (scope: string) => {
    const styles: Record<string, string> = {
      READ: 'bg-blue-100 text-blue-800',
      WRITE: 'bg-yellow-100 text-yellow-800',
      ADMIN: 'bg-purple-100 text-purple-800',
    };
    return (
      <span key={scope} className={`px-2 py-0.5 rounded text-xs font-medium ${styles[scope] || 'bg-gray-100 text-gray-800'}`}>
        {scope}
      </span>
    );
  };

  const filteredKeys = apiKeys.filter((key) =>
    key.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">API Keys</h1>
          <p className="text-gray-600 dark:text-gray-400">Manage API keys for programmatic access</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowCreateModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          Create API Key
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Keys</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
              </div>
              <Key className="w-8 h-8 text-blue-500" />
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Active Keys</p>
                <p className="text-2xl font-bold text-green-600">{stats.active}</p>
              </div>
              <Shield className="w-8 h-8 text-green-500" />
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Inactive Keys</p>
                <p className="text-2xl font-bold text-gray-600 dark:text-gray-300">{stats.inactive}</p>
              </div>
              <Activity className="w-8 h-8 text-gray-500" />
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Expired</p>
                <p className="text-2xl font-bold text-red-600">{stats.expired}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search API keys..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>

          {isMasterAdmin && (
            <OrganizationFilter
              value={organizationId}
              onChange={setOrganizationId}
              allowServiceProviderSelection={true}
            />
          )}

          <select
            value={activeFilter}
            onChange={(e) => setActiveFilter(e.target.value)}
            className="px-4 py-2 border dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          <button
            onClick={() => {
              fetchApiKeys();
              fetchStats();
            }}
            className="p-2 border dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* API Keys Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Key
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Scopes
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Last Used
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center">
                    <div className="flex justify-center">
                      <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                    </div>
                  </td>
                </tr>
              ) : filteredKeys.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                    No API keys found
                  </td>
                </tr>
              ) : (
                filteredKeys.map((key) => (
                  <tr key={key.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">{key.name}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 font-mono">{key.keyPrefix || '••••••••'}...</p>
                        {key.description && (
                          <p className="text-xs text-gray-400 mt-1">{key.description}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {key.scopes.map((scope) => getScopeBadge(scope))}
                      </div>
                    </td>
                    <td className="px-6 py-4">{getStatusBadge(key.active, key.expiresAt)}</td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{key.keyType}</td>
                    <td className="px-6 py-4">
                      {key.lastUsedAt ? (
                        <div className="text-sm">
                          <p className="text-gray-900 dark:text-white">
                            {new Date(key.lastUsedAt).toLocaleDateString()}
                          </p>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">Never</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        {key.active && (
                          <>
                            <button
                              onClick={() => handleRegenerate(key)}
                              className="p-1 text-gray-400 hover:text-yellow-600"
                              title="Regenerate"
                            >
                              <RefreshCw className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleRevoke(key)}
                              className="p-1 text-gray-400 hover:text-red-600"
                              title="Revoke"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => {
                            setSelectedKey(key);
                            setShowDeleteModal(true);
                          }}
                          className="p-1 text-gray-400 hover:text-red-600"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t dark:border-gray-700 flex justify-between items-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Page {page} of {totalPages}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-3 py-1 border dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 text-gray-700 dark:text-gray-300"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1 border dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 text-gray-700 dark:text-gray-300"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Create API Key</h2>
            </div>
            <div className="p-6 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="My API Key"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  rows={2}
                  placeholder="Optional description..."
                />
              </div>

              {/* Key Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Key Type</label>
                <select
                  value={formData.keyType}
                  onChange={(e) => setFormData({ ...formData, keyType: e.target.value as ApiKeyType })}
                  className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value={ApiKeyType.BUSINESS_KEY}>Business Key</option>
                  <option value={ApiKeyType.SERVICE_KEY}>Service Key</option>
                </select>
              </div>

              {/* Scopes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Scopes *</label>
                <div className="flex gap-3">
                  {SCOPE_OPTIONS.map((scope) => (
                    <label
                      key={scope}
                      className={`flex items-center gap-2 px-4 py-2 border rounded-lg cursor-pointer ${
                        formData.scopes?.includes(scope)
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                          : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                      } text-gray-900 dark:text-white`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.scopes?.includes(scope) || false}
                        onChange={() => toggleScope(scope)}
                        className="sr-only"
                      />
                      {scope}
                    </label>
                  ))}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  READ: View data, WRITE: Create/Update, ADMIN: Delete/Manage
                </p>
              </div>

              {/* Expiration */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Expiration Date (optional)
                </label>
                <input
                  type="date"
                  value={formData.expiresAt ? formData.expiresAt.split('T')[0] : ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      expiresAt: e.target.value ? `${e.target.value}T23:59:59Z` : undefined,
                    })
                  }
                  className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>
            <div className="p-6 border-t dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="px-4 py-2 border dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={!formData.name || !formData.scopes?.length}
                className="tl-btn tl-btn-primary disabled:opacity-50"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Secret Display Modal */}
      {showSecretModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-full">
                <Check className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">API Key Created</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Copy your secret key now</p>
              </div>
            </div>

            <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-yellow-800 dark:text-yellow-300">
                  This is the only time you'll see this secret key. Store it securely - you won't
                  be able to view it again.
                </p>
              </div>
            </div>

            <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4 mb-4">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Secret Key</label>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-sm font-mono break-all text-gray-900 dark:text-white">{newKeySecret}</code>
                <button
                  onClick={() => copyToClipboard(newKeySecret)}
                  className="p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                  title="Copy to clipboard"
                >
                  {secretCopied ? (
                    <Check className="w-5 h-5 text-green-600" />
                  ) : (
                    <Copy className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  )}
                </button>
              </div>
            </div>

            <button
              onClick={() => {
                setShowSecretModal(false);
                setNewKeySecret('');
              }}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedKey && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-full">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Delete API Key</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">This action cannot be undone</p>
              </div>
            </div>

            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Are you sure you want to delete the API key "{selectedKey.name}"? Any applications
              using this key will lose access.
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedKey(null);
                }}
                className="px-4 py-2 border dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiKeys;
